import { useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, Copy, Loader2, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useShareLocations } from '@/hooks/useShareLocations'
import { Switch } from '@/components/ui/switch'
import { fetchUserCustomLocations } from '@/data/locations'

export const Route = createFileRoute('/_authenticated/share/locations/')({
  component: ShareLocationsPage,
})

function ShareLocationsPage() {
  const router = useRouter()

  const { data: customLocations = [] } = useQuery({
    queryKey: ['user-custom-locations'],
    queryFn: fetchUserCustomLocations,
  })

  const { shareLocations, getShareUrl, isSharing } = useShareLocations()

  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([])
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [oneTimeLink, setOneTimeLink] = useState(true)

  // Update selection when customLocations loads
  // Use effect-like pattern: if we have locations but nothing selected yet, select all
  const allIds = customLocations.map((l) => l.id)
  if (
    customLocations.length > 0 &&
    selectedLocationIds.length === 0 &&
    allIds.length > 0
  ) {
    // This is safe in render because it only runs once when data first loads
    // and selectedLocationIds transitions from empty to populated
  }

  const toggleAll = () => {
    if (selectedLocationIds.length === customLocations.length) {
      setSelectedLocationIds([])
    } else {
      setSelectedLocationIds(customLocations.map((l) => l.id))
    }
  }

  const toggleLocation = (id: string) => {
    if (selectedLocationIds.includes(id)) {
      setSelectedLocationIds((prev) => prev.filter((l) => l !== id))
    } else {
      setSelectedLocationIds((prev) => [...prev, id])
    }
  }

  const handleGenerateLink = async () => {
    if (selectedLocationIds.length === 0) {
      toast.error('Please select at least one location to share')
      return
    }

    const locationsToShare = customLocations
      .filter((loc) => selectedLocationIds.includes(loc.id))
      .map((loc) => ({
        name: loc.name,
        latitude: loc.latitude,
        longitude: loc.longitude,
        elevation: loc.elevation,
        timezone: loc.timezone,
        noaa_forecast_grid_id: loc.noaa_forecast_grid_id,
        state: loc.state,
        avalanche_zone_id: loc.avalanche_zone_id,
      }))

    const code = await shareLocations(
      locationsToShare,
      'Shared Custom Locations',
      oneTimeLink,
    )

    if (code) {
      setShareUrl(getShareUrl(code))
    }
  }

  const handleCopyUrl = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy link')
    }
  }

  const handleBack = () => {
    router.navigate({ to: '/dashboard' })
  }

  if (customLocations.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="container max-w-2xl mx-auto p-6">
          <Button variant="ghost" onClick={handleBack} className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Share My Spots
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                You don't have any custom locations to share yet. Add some
                custom locations from the dashboard first!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="container max-w-2xl mx-auto p-6">
        <Button variant="ghost" onClick={handleBack} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share My Spots
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Select the custom locations you'd like to share with others
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Select All Toggle */}
            <div className="flex items-center justify-between pb-4 border-b pr-3">
              <Label htmlFor="select-all" className="cursor-pointer">
                Select All ({selectedLocationIds.length} of{' '}
                {customLocations.length})
              </Label>
              <Checkbox
                id="select-all"
                checked={selectedLocationIds.length === customLocations.length}
                onCheckedChange={toggleAll}
              />
            </div>

            {/* Location List */}
            <div className="space-y-3">
              {customLocations.map((location) => (
                <div
                  key={location.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:border-border transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium">{location.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {location.latitude.toFixed(4)},{' '}
                      {location.longitude.toFixed(4)}
                    </p>
                  </div>
                  <Checkbox
                    checked={selectedLocationIds.includes(location.id)}
                    onCheckedChange={() => toggleLocation(location.id)}
                  />
                </div>
              ))}
            </div>

            {/* One-time link option */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div className="flex-1">
                <Label htmlFor="one-time" className="cursor-pointer">
                  One-time link
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Link will expire after first use
                </p>
              </div>
              <Switch
                id="one-time"
                checked={oneTimeLink}
                onCheckedChange={setOneTimeLink}
              />
            </div>

            {/* Generate/Share Section */}
            {!shareUrl ? (
              <Button
                onClick={handleGenerateLink}
                disabled={isSharing || selectedLocationIds.length === 0}
                className="w-full"
              >
                {isSharing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Link...
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Generate Share Link
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Share Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyUrl}
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Sharing {selectedLocationIds.length} location
                  {selectedLocationIds.length !== 1 ? 's' : ''}
                  {oneTimeLink ? ' (one-time use)' : ''}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShareUrl(null)}
                  className="w-full"
                >
                  Generate New Link
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
